import { NotificationType } from '@app/components/common/Notification/Notification';
import { DefaultTheme } from 'styled-components';
import { Priority } from '../constants/enums/priorities';
import { UserModel, UserModeModel } from '@app/models/UserModel';

export const camelize = (string: string): string => {
  return string
    .split(' ')
    .map((word, index) => (index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1)))
    .join('');
};

export const getCurrencyPrice = (price: number, currency: string): string => {
  switch (currency) {
    case 'USD': {
      return `$${price}`;
    }

    default: {
      return `$${price}`;
    }
  }
};

type MarkArea = {
  xAxis: number;
};

export const getMarkAreaData = (data: string[] | number[]): MarkArea[][] =>
  data.map((el, index) => [
    {
      xAxis: 2 * index,
    },
    {
      xAxis: 2 * index + 1,
    },
  ]);

export const capitalize = (word: string): string => `${word[0].toUpperCase()}${word.slice(1)}`;

export const hexToRGB = (hex: string, alpha: string | number): string => {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  return alpha ? `rgba(${r}, ${g}, ${b}, ${alpha})` : `rgb(${r}, ${g}, ${b})`;
};

export const getDifference = (value: number, prevValue: number): string | null =>
  prevValue !== 0 ? `${((Math.abs(value - prevValue) / prevValue) * 100).toFixed(0)}%` : '100%';

export const normalizeProp = (prop: string | number | [number, number]): string =>
  typeof prop === 'number' ? `${prop}px` : (Array.isArray(prop) && `${prop[0]}px ${prop[1]}px`) || prop.toString();

export const defineColorByPriority = (priority: Priority, theme: DefaultTheme): string => {
  switch (priority) {
    case Priority.INFO:
      return theme.colors.main.primary;
    case Priority.LOW:
      return theme.colors.main.success;
    case Priority.MEDIUM:
      return theme.colors.main.warning;
    case Priority.HIGH:
      return theme.colors.main.error;
    default:
      return theme.colors.main.success;
  }
};

export const defineColorBySeverity = (severity: NotificationType | undefined, theme: DefaultTheme): string => {
  switch (severity) {
    case 'error':
      return theme.colors.main.error;
    case 'warning':
      return theme.colors.main.warning;
    case 'success':
      return theme.colors.main.success;
    case 'info':
      return theme.colors.main.primary;
    default:
      return theme.colors.main.primary;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mergeBy = (a: any[], b: any[], key: string): any[] =>
  a.filter((elem) => !b.find((subElem) => subElem[key] === elem[key])).concat(b);

export const getSmoothRandom = (factor: number, start: number): number => {
  const halfEnvelope = 1 / factor / 2;
  const max = Math.min(1, start + halfEnvelope);
  const min = Math.max(0, start - halfEnvelope);

  return Math.random() * (max - min) + min;
};

export const shadeColor = (color: string, percent: number): string => {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = parseInt(((R * (100 + percent)) / 100).toString());
  G = parseInt(((G * (100 + percent)) / 100).toString());
  B = parseInt(((B * (100 + percent)) / 100).toString());

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR = R.toString(16).length == 1 ? '0' + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length == 1 ? '0' + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length == 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
};

export const hexToHSL = (hex: string): { h: number; s: number; l: number } => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (result) {
    let r = parseInt(result[1], 16);
    let g = parseInt(result[2], 16);
    let b = parseInt(result[3], 16);
    (r /= 255), (g /= 255), (b /= 255);
    const max = Math.max(r, g, b),
      min = Math.min(r, g, b);
    let h, s;
    const l = (max + min) / 2;
    if (max == min) {
      h = s = 0; // achromatic
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0);
          break;
        case g:
          h = (b - r) / d + 2;
          break;
        case b:
          h = (r - g) / d + 4;
          break;
        default:
          h = 0;
      }
      h /= 6;
    }
    return {
      h,
      s,
      l,
    };
  } else {
    throw new Error('Non valid HEX color');
  }
};

export const getProfilePhoto = (imageUrl: string | null) => {
  return (
    imageUrl?.split(/[?]/)[0] || 'https://cdn.pixabay.com/photo/2016/08/08/09/17/avatar-1577909_960_720.png'
  );
};

export function secondsToString(seconds: number) {
  const time = {
    years: 0,
    months: 0,
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: seconds
  };

  if (time.seconds >= 60) {
    time.minutes = Math.floor(time.seconds / 60);
    time.seconds = time.seconds % 60;
  }
  if (time.minutes >= 60) {
    time.hours = Math.floor(time.minutes / 60);
    time.minutes = time.minutes % 60;
  }
  if (time.hours >= 24) {
    time.days = Math.floor(time.hours / 24);
    time.hours = time.hours % 24;
  }
  if (time.days >= 30) {
    time.months = Math.floor(time.days / 30);
    time.days = time.days % 30;
  }
  if (time.months >= 12) {
    time.years = Math.floor(time.months / 12);
    time.months = time.months % 12;
  }

  const units = [];
  if (time.years > 0) {
    units.push(`${Math.floor(time.years)} year${time.years > 1 ? 's' : ''}`);
  }
  if (time.months > 0) {
    units.push(`${Math.floor(time.months)} month${time.months > 1 ? 's' : ''}`);
  }
  if (time.days > 0) {
    units.push(`${Math.floor(time.days)} day${time.days > 1 ? 's' : ''}`);
  }
  if (time.hours > 0) {
    units.push(`${Math.floor(time.hours)} hour${time.hours > 1 ? 's' : ''}`);
  }
  if (time.minutes > 0) {
    units.push(`${Math.floor(time.minutes)} minute${time.minutes > 1 ? 's' : ''}`);
  }

  return units.join(', ') || '0 mins';
}

export function getModeDisplay(userMode?: UserModeModel) {
  if (!userMode) { return 'Not Set' }
  const modes = { 1: 'Calm', 2: 'Moderate', 3: 'Intense', 4: 'Custom' }
  let modeValues = ""
  if (userMode.mode_id === 4) {
    modeValues = ` (P: ${userMode.pressure}, T: ${userMode.speed})`
  }
  return modes[userMode.mode_id] + modeValues
}

export const parseUserExtraDetails = (record: UserModel) => {
  try {
    const details = JSON.parse(record.user_extra_details || '{}');
    return {
      ...record,
      platform: `${details.platform || ''} ${details.platformOSVersion || ''}`,
      app_version: details.appVersion || '',
    };
  } catch (error) {
    console.error('Error parsing user_extra_details:', error);
    return { ...record, app_version: '', platform: '' }; // Return the original record if parsing fails
  }
};